import { useNavigate } from 'react-router-dom'

import { Box, Button, useToggle } from '@factorialco/deprecated-design-system'
import { AtsCreatedApplicationsfromCandidatesFragment } from 'generated/resources/graphql'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import {
  TalentPoolTogglerCandidate,
  useTalentPoolToggler,
} from 'modules/ats/components/CandidateTalentPoolToggler/useTalentPoolToggler'
import { DeleteCandidateModal } from 'modules/ats/components/DeleteCandidateModal/DeleteCandidateModal'
import { AddCandidatesToJobPostingModal } from 'modules/ats/components/candidates/AddCandidateToJobPostingModal'
import { TalentPoolModal } from 'modules/ats/components/candidates/TalentPoolModal'

interface Props {
  candidate: TalentPoolTogglerCandidate & {
    fullName: string
    phoneNumber?: string | null
  }
}

export const CandidateActions = ({ candidate }: Props) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const [isAddToJobPostingOpen, toggleAddToJobPostingOpen] = useToggle(false)
  const [openDeleteCandidateModal, setOpenDeleteCandidateModal] = useToggle(false)
  const talentPool = candidate.talentPool
  const candidateId = candidate.id
  const {
    showTalentModalFunction,
    onTalentPoolModalSubmit,
    showTalentPoolModal,
    toggleShowTalentPoolModal,
    rememberConfirmationCheckbox,
    setRememberConfirmationCheckbox,
    updateCandidateTalentPoolValue,
  } = useTalentPoolToggler(candidate)

  const onClickCandidateDetails = () =>
    track(trackingEvents.ats.goToCandidatePersonal, { coming_from: 'candidates' })

  const onClickOnAddToJobOpening = () => {
    toggleAddToJobPostingOpen(true)()
    track(trackingEvents.ats.addCandidateToJobOpeningStart)
  }

  const onCloseAddToJobOpeningModal = () => {
    if (isAddToJobPostingOpen) {
      track(trackingEvents.ats.addCandidateToJobOpeningCancel)
    }
    toggleAddToJobPostingOpen(false)()
  }

  const onSuccessJobPostingModal = (
    applications: AtsCreatedApplicationsfromCandidatesFragment[]
  ) => {
    const application = applications[0]

    if (application?.id) {
      toggleAddToJobPostingOpen(false)()
      track(trackingEvents.ats.addCandidateToJobOpeningSubmit, { is_successful: true })
      navigate(
        navigation.ats.jobs.jobsId.applications.applicationDetail.fullPath({
          jobId: application.jobPosting.id,
          application_id: application.id,
        })
      )
    }
  }

  return (
    <>
      <Box flexDirection="row" gap="s8" justifyContent="flexEnd">
        <PopoverMenu type="list">
          <PopoverMenu.Trigger>
            <Button.Decorative
              type="secondary"
              aria-label={i18n.t('ats.candidates.more_actions', {
                candidate_name: candidate.fullName,
              })}
              iconName="ellipsis"
            />
          </PopoverMenu.Trigger>
          <PopoverMenu.Content side="bottom" align="center">
            <PopoverMenu.TouchableOption
              title={i18n.t('ats.candidate_details.add_to_job_opening')}
              onClick={onClickOnAddToJobOpening}
            />
            {talentPool ? (
              <PopoverMenu.TouchableOption
                title={i18n.t('ats.candidates.remove_from_talent_pool')}
                danger
                onClick={updateCandidateTalentPoolValue}
              />
            ) : (
              <PopoverMenu.TouchableOption
                title={i18n.t('ats.candidates.add_to_talent_pool')}
                onClick={showTalentModalFunction}
              />
            )}
            <PopoverMenu.TouchableOption
              title={i18n.t('ats.candidates.delete_modal.open_modal_button')}
              onClick={() => setOpenDeleteCandidateModal(true)()}
            />
          </PopoverMenu.Content>
        </PopoverMenu>

        <Button.Router
          type="secondary"
          aria-label={i18n.t('ats.candidates.candidate_details', {
            candidate_name: candidate.fullName,
          })}
          to={navigation.ats.candidates.id.applications.fullPath({ id: candidateId })}
          onClick={onClickCandidateDetails}
          iconName="arrow-right"
        />
      </Box>

      {isAddToJobPostingOpen && (
        <AddCandidatesToJobPostingModal
          candidateIds={[candidateId]}
          onSuccess={(applications) => onSuccessJobPostingModal(applications)}
          onClose={onCloseAddToJobOpeningModal}
        />
      )}

      {showTalentPoolModal && (
        <TalentPoolModal
          onClose={toggleShowTalentPoolModal}
          checked={rememberConfirmationCheckbox}
          onClick={() => {
            setRememberConfirmationCheckbox(!rememberConfirmationCheckbox)
          }}
          onSubmit={onTalentPoolModalSubmit}
        />
      )}

      {openDeleteCandidateModal && (
        <DeleteCandidateModal
          candidateId={candidate.id}
          onClose={() => setOpenDeleteCandidateModal(false)()}
        />
      )}
    </>
  )
}
